/**
 * Groups of localStorage keys the troubleshooting screens can safely wipe.
 *
 * App data: caches the app rebuilds on its own (transcripts, agent ids,
 * ElevenLabs voice/model lists, setup advisor history).
 * UI preferences: layout/view choices and "seen/dismissed" flags that only
 * change how screens open.
 *
 * Deliberately untouched: the auth session, drafts (voice clone, setup flow,
 * conversation annotations), genie setup progress (tested/skipped steps),
 * in-flight document processing state, email campaign ids and visitor
 * attribution/capture keys.
 */

const APP_DATA_KEYS = [
  "userAgents",
  "elevenlabs_voices_cache",
  "elevenlabs_llm_models_cache",
  "confirmation_advisor_history",
];
const APP_DATA_PREFIXES = ["transcript_"];

const UI_PREFERENCE_KEYS = [
  "nav-mode",
  "sidebarOpen",
  "sidebarEverExpanded",
  "liveAgentTranscriptOpen",
  "genie-view-mode",
  "hg.conversations.view-mode",
  "dashboard-selected-genie",
  "helpgenie_preferred_currency",
  "helpgenie:tour:furthest-slide",
];
const UI_PREFERENCE_PREFIXES = [
  "hg-genie-mode-",
  "hg:activateFabDismissed:",
  "hg:suite-explainer-seen:",
  "genie-welcome-seen:",
  "helpgenie.installBanner.",
];

const removeMatching = (keys: string[], prefixes: string[]): number => {
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (keys.includes(key) || prefixes.some((prefix) => key.startsWith(prefix)))) {
      toRemove.push(key);
    }
  }
  toRemove.forEach((key) => localStorage.removeItem(key));
  return toRemove.length;
};

/** Removes cached app data and returns how many keys were cleared. */
export const clearAppData = (): number => removeMatching(APP_DATA_KEYS, APP_DATA_PREFIXES);

/** Removes saved UI preferences and returns how many keys were cleared. */
export const resetUiPreferences = (): number => removeMatching(UI_PREFERENCE_KEYS, UI_PREFERENCE_PREFIXES);
